import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import { ChevronRight, AlertTriangle, ExternalLink } from "lucide-react";

// Anchor IDs used by the table of contents and (later) by each section heading.
const TOC = [
  { id: "droit-sejour",      label: "Le droit de séjour en Pologne selon votre provenance" },
  { id: "karta-pobytu",      label: "La karta pobytu (carte de séjour)" },
  { id: "pesel",             label: "Le numéro PESEL en Pologne" },
  { id: "nip",               label: "Le numéro NIP" },
  { id: "banque",            label: "Ouvrir un compte bancaire" },
  { id: "permis",            label: "Le permis de conduire" },
  { id: "biens",             label: "Importer vos biens" },
  { id: "famille",           label: "Se marier et fonder une famille en Pologne" },
  { id: "nationalite",       label: "Obtenir la nationalité polonaise" },
  { id: "residence-fiscale", label: "Résidence fiscale" },
];

const KEY_POINTS = [
  "UE, EEE et Suisse : pas de visa ni de carte de séjour pour vivre en Pologne. Enregistrement du séjour obligatoire au-delà de 3 mois.",
  "Le PESEL (numéro d'identification à 11 chiffres) est indispensable pour tout : banque, fisc, démarches en ligne.",
  "Le NIP n'est nécessaire que si vous créez une entreprise ou êtes assujetti à la TVA. Sinon, votre PESEL suffit.",
  "Votre permis de conduire UE ou suisse reste valable en Pologne - l'échange est facultatif, sans examen.",
  "Résidence fiscale polonaise : centre des intérêts vitaux en Pologne OU plus de 183 jours par an sur le territoire.",
];

const RECAP_ROWS = [
  ["Droit de séjour",        "Enregistrement du séjour au-delà de 3 mois",  "Identique à l'UE",            "Bénéficiaire de l'accord de retrait", "Visa D puis carte de séjour"],
  ["Karta pobytu",           "Non, certificat UE",                          "Non, certificat UE",          "Document de bénéficiaire",            "Oui"],
  ["PESEL",                  "Oui",                                         "Oui",                         "Oui",                                 "Oui"],
  ["NIP",                    "Seulement si activité ou TVA",                "Identique",                   "Identique",                           "Identique"],
  ["Compte bancaire",        "Carte d'identité possible",                   "Carte d'identité possible",   "Passeport et PESEL",                  "Passeport, titre et PESEL"],
  ["Permis de conduire",     "Echange facultatif, sans examen",             "Identique à l'UE",            "Echange sans examen",                 "Echange sans examen sous 185 jours"],
  ["Biens de déménagement",  "Libre circulation",                           "Exonération pays tiers",      "Exonération pays tiers",              "Exonération pays tiers"],
];

const SOURCES = [
  { url: "https://udsc.gov.pl/en/cudzoziemcy/obywatele-ue-oraz-eogszwajcarii-i-czlonkowie-ich-rodzin/wymagane-formalnosci/obywatele-ue/zarejestrowanie-pobytu/", label: "UDSC, enregistrement de séjour d'un citoyen de l'UE", description: "Source officielle de l'Office des étrangers. Elle pose l'obligation, pour un citoyen de l'UE qui séjourne plus de trois mois, d'enregistrer son séjour auprès du voïvode, fixe le délai de dépôt au lendemain de l'expiration des trois mois et liste les pièces selon le motif." },
  { url: "https://www.gov.pl/web/mswia-en/registration-of-residence", label: "Ministère de l'Intérieur (gov.pl), Registration of residence", description: "Confirme que l'obligation d'enregistrement vise aussi les membres de famille ainsi que les citoyens de l'EEE et de la Confédération suisse. Elle précise aussi les justificatifs demandés." },
  { url: "https://migrant.info.pl/en/Residence_in_Poland/legalization-of-stay-of-foreigner-s-family-members/residence-registration-and-permanent-residence-permit-of-an-eu-citizen", label: "migrant.info.pl, enregistrement et séjour permanent d'un citoyen UE", description: "Établit la gratuité de la procédure, la délivrance d'un certificat valable 10 ans et l'acquisition du droit de séjour permanent après cinq ans de séjour ininterrompu." },
  { url: "https://migrant.poznan.uw.gov.pl/en/slownik-pojec/withdrawal-agreement-beneficiary", label: "UDSC, bénéficiaire de l'accord de retrait", description: "Définit qui est bénéficiaire de l'accord de retrait entre l'UE et le Royaume-Uni (résidence légale avant le 1er janvier 2021) ainsi que l'étendue des droits conservés." },
  { url: "https://www.gov.uk/living-in-poland", label: "gov.uk, Living in Poland", description: "Recommande d'obtenir le dokument pobytowy dla beneficjenta umowy wystąpienia, signale l'exemption des contrôles EES pour les bénéficiaires et rappelle que les arrivants postérieurs relèvent du régime des pays tiers." },
  { url: "https://home-affairs.ec.europa.eu/policies/migration-and-asylum/eu-immigration-portal/family-member-poland_en", label: "Portail européen de l'immigration (Commission européenne), Family member in Poland", description: "Décrit les conditions du permis de séjour temporaire pour regroupement familial au profit d'un ressortissant de pays tiers." },
  { url: "https://www.gov.pl/web/gov/uzyskaj-numer-pesel--usluga-dla-cudzoziemcow-en", label: "gov.pl, obtenir un numéro PESEL (service pour étrangers)", description: "Source officielle. Elle décrit les deux voies d'obtention du PESEL, l'attribution d'office via l'enregistrement d'adresse au-delà de trente jours ou la demande avec base légale, sa gratuité et son extension aux citoyens de l'EEE, suisses et britanniques." },
  { url: "https://www.biznes.gov.pl/en/portal/004124", label: "Biznes.gov.pl, qu'est-ce que le NIP", description: "Établit que le PESEL est l'identifiant fiscal des personnes physiques sans activité économique et que le NIP est réservé aux entrepreneurs, aux assujettis à la TVA et aux entités." },
  { url: "https://www.oecd.org/content/dam/oecd/en/topics/policy-issue-focus/aeoi/poland-tin.pdf", label: "OCDE, Poland, Information on Tax Identification Numbers (PDF)", description: "Confirme que, depuis le 1er septembre 2011, le PESEL fait office de numéro fiscal pour les personnes physiques non assujetties à la TVA et sans activité économique." },
  { url: "https://www.podatki.gov.pl/informacje-o-cle/podstawowe-informacje-o-cle/informacje-dla-osob-fizycznych/mienie-przesiedlenia-zwolnienie-z-cla/", label: "podatki.gov.pl, mienie przesiedlenia", description: "Source officielle de l'administration fiscale et douanière. Elle fixe les conditions de l'exonération lors d'un transfert de résidence depuis un pays tiers, dont l'obligation de non-cession pendant douze mois. La base juridique est le règlement (CE) numéro 1186/2009." },
  { url: "https://warszawa19115.pl/-/exchanging-a-foreign-driving-licence-issued-by-a-member-state-of-the-european-union-the-swiss-confederation-efta-a-state-party-to-the-convention-on-road-traffic-or-a-corresponding-model-driving-licence-as-defined-in-these-conventions-for-a-polish-driving-", label: "Warszawa 19115, échange d'un permis UE, suisse, EFTA ou des conventions", description: "Service municipal officiel. Il indique les pièces de l'échange et l'absence d'examen pour les permis de l'UE, de l'EEE et suisses." },
  { url: "https://warszawa19115.pl/-/exchange-of-a-foreign-driving-licence-not-specified-in-the-traffic-conventions-into-a-polish-driving-licence", label: "Warszawa 19115, échange d'un permis non visé par les conventions", description: "Pose la règle de l'échange obligatoire après 185 jours de résidence et la perte de validité du permis étranger passé ce délai, y compris pour les permis britanniques émis après le 31 décembre 2020." },
  { url: "https://migrant.legal/en/planning-to-marry-a-foreigner-in-poland-heres-what-you-need-to-know/", label: "migrant.legal, se marier avec un étranger en Pologne", description: "Détaille le certificat de capacité matrimoniale, la possibilité de dispense par le tribunal de famille, l'exigence de traductions et d'interprète assermentés." },
  { url: "https://migrant.legal/en/married-a-foreigner-abroad-will-it-be-valid-in-poland-and-how-to-register-it/", label: "migrant.legal, reconnaissance d'un mariage conclu à l'étranger", description: "Décrit la transcription à l'USC, avec apostille ou légalisation et traduction assermentée." },
  { url: "https://notesfrompoland.com/2025/10/17/polish-government-proposes-new-rights-for-unmarried-partners-including-same-sex-couples/", label: "Notes from Poland, projet de loi sur les couples non mariés (octobre 2025)", description: "Établit l'absence d'union civile ou de partenariat enregistré en droit polonais et l'état du projet de contrat de cohabitation." },
  { url: "https://www.visahq.com/news/2025-12-30/pl/poland-approves-cohabitation-contract-bill-offering-limited-recognition-to-expat-and-same-sex-partners/", label: "VisaHQ, adoption du projet de contrat de cohabitation (décembre 2025)", description: "Rapporte l'adoption en conseil des ministres le 30 décembre 2025 d'un dispositif aux droits limités." },
  { url: "https://www.gov.pl/web/mswia-en/apply-to-be-recognised-as-a-polish-citizen", label: "gov.pl, être reconnu comme citoyen polonais", description: "Source officielle actuelle. Elle détaille les critères de la reconnaissance comme citoyen (notamment trois ans de séjour permanent avec revenus stables et logement, ou deux ans en étant marié depuis au moins trois ans à un citoyen polonais), l'exigence d'un niveau de polonais B1, les frais de 219 PLN et la traduction assermentée des documents étrangers." },
  { url: "https://wsoic.lodzkie.eu/en/sprawy-obywatelskie/uznanie-za-obywatela-polskiego/", label: "Office de voïvodie de Łódź, reconnaissance comme citoyen polonais", description: "Détaille les pièces et l'exigence d'un certificat de polonais niveau B1." },
  { url: "https://taxsummaries.pwc.com/poland/individual/residence", label: "PwC Worldwide Tax Summaries, Poland, Individual residence", description: "Établit les deux critères alternatifs de résidence fiscale, le centre des intérêts vitaux ou plus de 183 jours sur l'année fiscale." },
  { url: "https://grantthornton.pl/en/article/tax-explanations-determining-tax-residency-in-poland/", label: "Ministère des Finances et Grant Thornton, explications fiscales sur la résidence (2021)", description: "Établit la primauté pratique du centre des intérêts vitaux et la distinction entre obligation fiscale illimitée et limitée." },
];

// Small reusable building blocks ----------------------------------------------

function PL({ children }) {
  // Italic styling for inline Polish terms - keeps the typography consistent.
  return <em className="italic text-slate-700">{children}</em>;
}

function Section({ id, n, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-14" data-testid={`section-${id}`}>
      <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mb-5">
        <span className="text-slate-400 mr-2 tabular-nums">{n}.</span>
        {title}
      </h2>
      <div className="space-y-4 text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Sub({ children }) {
  return <h3 className="font-display text-lg font-semibold text-slate-900 mt-6 mb-2">{children}</h3>;
}

function Warn({ children, testid }) {
  return (
    <aside
      className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4 flex items-start gap-3"
      data-testid={testid}
    >
      <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-amber-900 leading-relaxed">{children}</p>
    </aside>
  );
}

// -----------------------------------------------------------------------------

export default function Demarches() {
  const { t, lp } = useT();
  void t;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="demarches-page">
      {/* Hero */}
      <header className="mb-12" data-testid="demarches-hero">
        <span className="hej-chip" data-testid="demarches-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Mis à jour janvier 2026
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          Démarches administratives en Pologne
        </h1>
        <p className="text-slate-600 mt-4 leading-relaxed text-lg">
          Titre de séjour, PESEL, NIP, compte bancaire, permis de conduire - tout ce qu'il faut régler pour s'installer en Pologne.
        </p>
      </header>

      {/* Sélecteur de parcours selon la nationalité */}
      <section className="mb-12" data-testid="demarches-path-picker">
        <p className="text-slate-600 leading-relaxed mb-5">
          Vos formalités dépendent de votre nationalité. Choisissez votre situation.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 items-stretch">
          {/* Carte 1 : utilisateurs de la page courante */}
          <div
            className="h-full bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-7 flex flex-col"
            data-testid="path-card-ue"
          >
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
              Cette page
            </span>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              UE, EEE, Suisse et Royaume-Uni
            </h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Libre circulation et formalités allégées : séjour, PESEL, banque, permis et impôts.
            </p>
          </div>

          {/* Carte 2 : renvoi vers la page pays tiers */}
          <Link
            to={lp("/demarches-pays-tiers")}
            className="h-full bg-white border border-slate-200 rounded-2xl p-6 md:p-7 flex flex-col hover:border-[#0056D2] hover:shadow-md transition group"
            data-testid="path-card-pays-tiers"
          >
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
              Autre nationalité
            </span>
            <h3 className="font-display text-lg font-semibold text-slate-900 group-hover:text-[#0056D2] transition">
              Pays tiers (hors UE)
            </h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Le parcours complet : visa, permis de travail, carte de séjour et étapes après l'arrivée.
            </p>
            <span className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-[#0056D2] group-hover:gap-2 transition-all">
              Voir le guide
              <ChevronRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </section>

      {/* À retenir */}
      <section className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 mb-12" data-testid="demarches-key-points">
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">À retenir</h2>
        <ul className="space-y-3">
          {KEY_POINTS.map((point, i) => (
            <li key={i} className="flex items-start gap-3 text-slate-700 leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#DC143C] flex-shrink-0" aria-hidden="true"></span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Sommaire */}
      <section className="mb-16" data-testid="demarches-toc">
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Sommaire</h2>
        <ol className="space-y-2 list-none">
          {TOC.map((item, i) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className="group flex items-baseline gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 transition"
                data-testid={`toc-${item.id}`}
              >
                <span className="font-display text-sm font-semibold text-slate-400 group-hover:text-[#0056D2] tabular-nums w-6">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-slate-700 group-hover:text-[#0056D2] flex-1">{item.label}</span>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#0056D2] self-center" />
              </a>
            </li>
          ))}
        </ol>
      </section>

      {/* ============================================================ */}
      {/* Sections détaillées */}
      {/* ============================================================ */}

      <Section id="droit-sejour" n={1} title="Le droit de séjour en Pologne selon votre provenance">
        <p>C'est l'étape qui commande toutes les autres.</p>

        <Sub>1.1 Ressortissants de l'UE et de l'EEE</Sub>
        <p>
          Au titre de la libre circulation, vous n'avez besoin ni de visa ni de carte de séjour pour vivre, travailler ou étudier en
          Pologne. Une seule obligation pèse sur vous pour un séjour de plus de trois mois : enregistrer votre séjour auprès de l'office
          de voïvodie (<PL>urząd wojewódzki</PL>) de votre lieu de résidence. Vous déposez la demande en personne, au plus tard le
          lendemain de l'expiration du délai de trois mois suivant votre entrée. Les mineurs sont dispensés du dépôt en personne. La
          procédure est gratuite. Elle débouche sur un certificat d'enregistrement de séjour d'un citoyen de l'UE
          (<PL>zaświadczenie o zarejestrowaniu pobytu obywatela UE</PL>), valable 10 ans.
        </p>
        <p>
          Les pièces varient selon le motif. Pour travailler, les citoyens de l'UE accèdent librement au marché du travail, sans permis,
          et vous justifiez simplement d'un emploi salarié ou d'une activité indépendante. Pour étudier, vous présentez une attestation
          d'inscription, une preuve de ressources suffisantes et une assurance maladie. Pour rejoindre votre famille, votre conjoint et
          vos proches demandent le titre adapté une fois votre propre séjour enregistré, et un membre de famille qui n'est pas citoyen
          de l'UE reçoit une carte de séjour de membre de famille d'un citoyen de l'UE.
        </p>
        <p className="font-semibold text-slate-900">
          Après cinq ans de séjour ininterrompu, vous obtenez le droit de séjour permanent. Le régime vaut à l'identique pour l'EEE,
          c'est-à-dire la Norvège, l'Islande et le Liechtenstein.
        </p>
        <p>
          Si vous vous installez pour votre retraite, notre guide pour{" "}
          <Link to={lp("/retraite-pologne")} className="text-[#0056D2] hover:underline">prendre sa retraite en Pologne</Link>{" "}
          reprend ces démarches sous l'angle des pensionnés, avec la fiscalité de la pension et la couverture santé.
        </p>
        <p>
          Si votre installation passe par un emploi, notre page{" "}
          <Link to={lp("/travailler-pologne")} className="text-[#0056D2] hover:underline">Travailler en Pologne</Link>{" "}
          rassemble les salaires, les contrats et les droits du salarié.
        </p>

        <Sub>1.2 Ressortissants suisses</Sub>
        <p>
          Les Suisses relèvent du même régime que les citoyens de l'UE et de l'EEE, au titre de l'accord de libre circulation conclu
          avec l'Union européenne. Pas de visa, pas de carte de séjour, un simple enregistrement du séjour au-delà de trois mois, selon
          les mêmes règles et le même calendrier. Les membres de la famille à charge en bénéficient aussi.
        </p>

        <Sub>1.3 Ressortissants du Royaume-Uni</Sub>
        <p>Le Brexit a créé deux situations très différentes.</p>
        <p>
          <strong className="text-slate-900">Premier cas - bénéficiaire de l'accord de retrait :</strong>{" "}
          Vous résidiez légalement en Pologne avant le 1er janvier 2021. Vous gardez des droits proches de ceux d'un citoyen de l'UE,
          pour travailler, étudier ou séjourner. Mieux vaut détenir le titre dédié, le{" "}
          <PL>dokument pobytowy dla beneficjenta umowy wystąpienia</PL>, qui prouve votre statut et vous dispense des contrôles renforcés
          aux frontières (systèmes EES et ETIAS).
        </p>
        <p>
          <strong className="text-slate-900">Second cas - arrivée à partir du 1er janvier 2021 :</strong>{" "}
          Vous êtes ressortissant de pays tiers. La circulation sans visa se limite aux courts séjours, 90 jours sur 180. Pour vous
          installer, il faut un visa national de type D, puis une carte de séjour correspondant à votre motif. Pour travailler, un
          permis de travail est en principe nécessaire, sauf catégories dispensées.
        </p>
      </Section>

      <Section id="karta-pobytu" n={2} title="La karta pobytu (carte de séjour)">
        <p>
          La <PL>karta pobytu</PL> ne concerne que les ressortissants de pays tiers. Parmi les provenances traitées ici, cela vise donc
          seulement les Britanniques arrivés après le 1er janvier 2021. Les citoyens de l'UE, de l'EEE et les Suisses n'en reçoivent
          pas, puisque leur document est le certificat d'enregistrement de séjour vu plus haut.
        </p>
        <p>
          C'est le document physique qui matérialise une décision d'autorisation de séjour. Le séjour temporaire
          (<PL>karta czasowego pobytu</PL>) s'accorde selon le motif. Un contrat ou un permis pour le travail. Une inscription en
          études supérieures à temps plein pour les études. Le regroupement familial pour la famille, prévu par l'article 159 de la loi
          sur les étrangers, au profit du conjoint, des enfants mineurs et, dans des cas limités, de personnes à charge, à condition que
          le membre de famille déjà présent détienne un titre valable. Le séjour permanent (<PL>karta stałego pobytu</PL>) vient
          ensuite, après plusieurs années, ou plus vite après un mariage avec un citoyen polonais.
        </p>
        <p className="font-semibold text-slate-900">
          La demande se dépose à l'office de voïvodie, avec relevé d'empreintes. Les délais réels sont souvent longs, plusieurs mois.
        </p>
        <p>
          Prévoyez les justificatifs habituels : passeport, photos, preuve de ressources, assurance maladie et preuve d'un logement. Les
          documents étrangers doivent en général être traduits par un traducteur assermenté (<PL>tłumacz przysięgły</PL>).
        </p>
      </Section>

      <Section id="pesel" n={3} title="Le numéro PESEL en Pologne">
        <p>
          Le PESEL (<PL>Powszechny Elektroniczny System Ewidencji Ludności</PL>) est l'identifiant personnel à onze chiffres. Sans lui,
          presque rien ne se fait : compte bancaire, déclarations fiscales, démarches en ligne via le profil de confiance
          (<PL>Profil Zaufany</PL>), accès aux services publics. Il concerne tout le monde, quelle que soit la provenance.
        </p>
        <p>Deux voies existent, toutes deux gratuites.</p>
        <p>
          <strong className="text-slate-900">Voie automatique :</strong>{" "}
          En enregistrant votre adresse (<PL>zameldowanie</PL>) à la mairie ou à la commune (<PL>urząd gminy</PL> ou <PL>urząd miasta</PL>)
          pour un séjour de plus de trente jours, vous recevez le PESEL d'office. Il faut alors un bail signé par le propriétaire du
          logement.
        </p>
        <p>
          <strong className="text-slate-900">Demande explicite :</strong>{" "}
          Utile si vous ne pouvez pas enregistrer d'adresse mais qu'une administration, banque, fisc ou ZUS, exige un PESEL. Vous
          remplissez un formulaire, en personne, dans n'importe quelle commune, en indiquant la base légale de l'obligation.
        </p>
        <Warn testid="warn-pesel">
          <strong>Attention</strong> : ne pas confondre deux démarches distinctes. L'enregistrement du séjour
          (<PL>zarejestrowanie pobytu</PL>), à la voïvodie, produit le certificat de séjour UE. L'enregistrement de l'adresse
          (<PL>zameldowanie</PL>), à la commune, produit le PESEL. Ce sont deux guichets et deux papiers différents.
        </Warn>
      </Section>

      <Section id="nip" n={4} title="Le numéro NIP">
        <p>
          Le NIP (<PL>Numer Identyfikacji Podatkowej</PL>, dix chiffres) est réservé aux entités et aux personnes qui exercent une
          activité économique ou sont assujetties à la TVA.
        </p>
        <p className="font-semibold text-slate-900">
          Pour un particulier qui s'installe sans créer d'entreprise, votre identifiant fiscal est votre PESEL, pas un NIP.
        </p>
        <p>
          Depuis la réforme du 1er septembre 2011, les personnes physiques sans activité et non assujetties à la TVA utilisent leur
          PESEL pour l'impôt sur le revenu. Un NIP ne devient nécessaire que dans des cas particuliers : activité indépendante,
          assujettissement à la TVA, ou demande expresse du bureau des impôts (formulaire NIP-7). La plupart des gens qui viennent
          travailler, étudier ou rejoindre leur famille n'ont donc aucun NIP à demander.
        </p>
      </Section>

      <Section id="banque" n={5} title="Ouvrir un compte bancaire">
        <p>
          Aucune loi n'interdit à un étranger d'avoir un compte en Pologne. Mais chaque banque applique ses propres règles de
          vérification (procédures KYC et lutte anti-blanchiment), et les pratiques varient beaucoup.
        </p>
        <p>
          La pièce d'identité d'abord : passeport pour tous, carte nationale d'identité souvent acceptée pour les citoyens de l'UE, de
          l'EEE et les Suisses.
        </p>
        <p>Un numéro de téléphone polonais est presque toujours demandé, pour les codes SMS et l'activation de l'application.</p>
        <p>Un justificatif d'adresse en Pologne est utile, parfois exigé.</p>
        <p className="font-semibold text-slate-900">
          Le PESEL n'est pas toujours obligatoire à l'ouverture, mais il facilite tout. Sans lui, certaines banques n'ouvrent qu'un
          compte de non-résident aux fonctions limitées, à reclasser une fois le PESEL obtenu.
        </p>
        <p>
          Pour un ressortissant de pays tiers comme un Britannique récemment arrivé, le titre de séjour ou le visa peut être réclamé.
          Les documents rédigés dans une autre langue peuvent devoir être présentés en traduction assermentée.
        </p>
        <p>
          Les banques les plus utilisées par les expatriés : PKO BP, mBank, ING Bank Śląski, Santander Bank Polska. Revolut et Wise sont
          également très répandus comme solution complémentaire.
        </p>
      </Section>

      <Section id="permis" n={6} title="Le permis de conduire">
        <p>Tout dépend du pays qui a délivré votre permis.</p>
        <p>
          <strong className="text-slate-900">Permis UE, EEE ou suisse :</strong>{" "}
          Reste valable jusqu'à sa date d'expiration, sans aucune obligation d'échange. Vous pouvez l'échanger si vous le souhaitez,
          sans examen, uniquement sur dossier.
        </p>
        <p>
          <strong className="text-slate-900">Permis britannique (émis avant le 31/12/2020) :</strong>{" "}
          L'échange est possible sans examen. Passé 185 jours de résidence, l'échange devient obligatoire. Le délai se compte à partir
          de votre <PL>zameldowanie</PL>, pas de votre date d'arrivée.
        </p>
        <p>
          <strong className="text-slate-900">Permis britannique (émis après le 31/12/2020) :</strong>{" "}
          Ces permis relèvent du régime des pays tiers. Un échange contre un permis polonais implique en principe un examen théorique.
          Passé 185 jours de résidence, le permis perd sa validité sur le territoire polonais. Vérifiez auprès du{" "}
          <PL>Wydział Komunikacji</PL> de votre ville avant d'engager la démarche.
        </p>
        <Warn testid="warn-permis">
          Le piège est le délai. Passé 185 jours sans échange, la procédure simplifiée tombe. Comme l'original du permis est remis au
          dépôt du dossier et que le traitement prend plusieurs semaines, anticipez largement.
        </Warn>
        <p>
          La démarche se fait au service des transports (<PL>Wydział Komunikacji</PL>) de votre commune. Il faut un formulaire, une
          photo aux normes, une copie du permis avec l'original pour vérification, un justificatif de séjour et d'adresse ainsi que le
          paiement. Pour un permis britannique, l'administration vérifie le document auprès de l'autorité émettrice.
        </p>
      </Section>

      <Section id="biens" n={7} title="Importation de vos biens personnels">
        <p>Le traitement douanier dépend du pays de départ, pas de votre nationalité.</p>
        <p>
          <strong className="text-slate-900">Depuis un État de l'UE :</strong>{" "}
          Vos biens personnels usagés circulent librement, sans droits de douane ni TVA à l'importation. Aucune formalité douanière
          particulière.
        </p>
        <p>
          <strong className="text-slate-900">Depuis un pays tiers (Suisse, Royaume-Uni) :</strong>{" "}
          Le dédouanement est obligatoire. Vous pouvez bénéficier de l'exonération dite des biens de déménagement
          (<PL>mienie przesiedlenia</PL>), prévue par le règlement (CE) numéro 1186/2009.
        </p>
        <p>
          L'exonération suppose plusieurs conditions réunies : vous transférez votre résidence d'un pays tiers vers la Pologne après y
          avoir vécu au moins douze mois consécutifs ; les biens vous appartenaient et servaient à votre ancien domicile depuis au moins
          six mois ; ils sont destinés à un usage personnel, pas à la revente ; et vous vous engagez à ne pas les céder pendant douze
          mois après la mise en libre circulation.
        </p>
        <p>
          L'exonération de droits de douane, celle de TVA à l'importation et celle d'accise reposent sur des bases juridiques distinctes
          et s'apprécient séparément. Pour un véhicule, il doit avoir été détenu et utilisé au moins six mois, et l'accise peut rester
          due. La déclaration se fait auprès du bureau de douane polonais. Bon à savoir : le conjoint n'est pas considéré comme un
          tiers pour la condition de non-cession.
        </p>
      </Section>

      <Section id="famille" n={8} title="Se marier et fonder une famille en Pologne">
        <p>
          Le mariage civil se conclut devant l'officier de l'état civil (<PL>Urząd Stanu Cywilnego</PL>, ou <PL>USC</PL>). Un mariage
          religieux à effets civils, dit concordataire, est possible aussi. Le document clé pour un étranger est le certificat de
          capacité matrimoniale (<PL>zaświadczenie o zdolności prawnej do zawarcia małżeństwa</PL>), qui atteste qu'aucun empêchement
          n'existe au regard du droit de son pays d'origine. Si votre pays ne délivre pas ce certificat, vous pouvez saisir le tribunal
          de famille pour en être dispensé.
        </p>
        <p>
          Les autres pièces sont classiques : passeport, acte de naissance, le certificat ci-dessus et, pour les personnes divorcées ou
          veuves, les actes correspondants. Tout document étranger doit être accompagné d'une traduction assermentée en polonais. Si
          l'un des futurs époux ne maîtrise pas le polonais, un interprète assermenté est requis au dépôt et pendant la cérémonie.
          Comptez environ un mois d'attente après la déclaration d'intention.
        </p>
        <p>
          Un mariage célébré à l'étranger ne produit ses effets en Pologne qu'une fois transcrit à l'<PL>USC</PL>. Il faut l'acte de
          mariage original, avec apostille ou légalisation selon le pays, accompagné d'une traduction assermentée. Les documents issus
          de l'UE sont souvent dispensés de légalisation.
        </p>
        <p className="font-semibold text-slate-900">
          Le mariage avec un citoyen polonais ne donne pas automatiquement de droit de séjour ni la nationalité. Il ouvre droit à une
          carte de séjour temporaire pour conjoint, valable jusqu'à trois ans, et accélère ensuite l'accès au séjour permanent puis à la
          nationalité.
        </p>

        <Sub>Union civile et couples non mariés</Sub>
        <Warn testid="warn-famille">
          A la date de cette mise à jour, la Pologne ne consacre pas, dans son droit interne général, un mariage ou un partenariat
          enregistré de même sexe comparable au PACS français. Un projet de loi sur un contrat de cohabitation, ouvert à tout couple y
          compris de même sexe, a été adopté en conseil des ministres fin décembre 2025, mais se heurte à une opposition présidentielle
          et n'est pas en vigueur. En revanche, des développements judiciaires récents (arrêt CJUE fin 2025, première transcription d'un
          mariage de même sexe à Varsovie en mai 2026) imposent désormais la reconnaissance, pour certains effets administratifs, des
          mariages de même sexe valablement conclus dans un autre État de l'UE. Cette matière évolue rapidement - vérifiez la situation
          à la date de votre projet.
        </Warn>
      </Section>

      <Section id="nationalite" n={9} title="Obtenir la nationalité polonaise">
        <p>La Pologne admet la double nationalité. Deux voies mènent au passeport.</p>
        <p>
          <strong className="text-slate-900">La reconnaissance comme citoyen (<PL>uznanie za obywatela</PL>) :</strong>{" "}
          C'est une décision du voïvode soumise à des critères légaux. Par exemple, trois ans de séjour ininterrompu sous séjour
          permanent, avec des revenus stables et réguliers ainsi qu'un titre légal d'occupation d'un logement. Autre exemple : deux ans
          sous séjour permanent en étant marié depuis au moins trois ans à un citoyen polonais. Dans tous les cas, il faut prouver une
          maitrise du polonais au niveau B1 par un certificat officiel. Les frais sont de 219 PLN.
        </p>
        <p>
          <strong className="text-slate-900">L'octroi par le président de la République (<PL>nadanie</PL>) :</strong>{" "}
          Cette voie est discrétionnaire, sans critères fixes. La décision est définitive et sans recours. Selon le lieu de dépôt, des
          frais consulaires et de traduction peuvent s'ajouter.
        </p>
        <p>
          Les citoyens de l'UE, de l'EEE et les Suisses qui ont acquis le droit de séjour permanent après cinq ans peuvent s'appuyer sur
          ce statut comme base de la reconnaissance. La demande se dépose auprès du voïvode, ou du consul depuis l'étranger, et tout
          document étranger doit être traduit par un traducteur assermenté.
        </p>
      </Section>

      <Section id="residence-fiscale" n={10} title="Impôts à titre personnel">
        <Warn testid="warn-residence-fiscale-intro">
          Ce qui suit est une information générale, pas un conseil fiscal individualisé. Pour une situation transfrontalière, parlez-en
          à un professionnel.
        </Warn>
        <p>Vous êtes résident fiscal polonais si vous remplissez l'un de ces deux critères, qui sont alternatifs et non cumulatifs :</p>
        <p>
          <strong className="text-slate-900">Séjour de plus de 183 jours :</strong>{" "}
          en Pologne sur l'année fiscale (année civile).
        </p>
        <p>
          <strong className="text-slate-900">Centre des intérêts vitaux :</strong>{" "}
          personnels ou économiques situé en Pologne. En pratique, l'administration et les tribunaux font primer ce critère. Transférer
          sa famille et sa vie en Pologne peut suffire à y créer la résidence fiscale, même avant 183 jours.
        </p>
        <p>
          La portée de l'impôt découle de ce statut. Le résident est soumis à une obligation fiscale illimitée, donc imposable sur ses
          revenus mondiaux. Le non-résident n'est imposé que sur ses revenus de source polonaise.
        </p>
        <p>
          La Pologne est liée par des conventions de non-double imposition avec la Suisse et avec le Royaume-Uni. Elles répartissent le
          droit d'imposer et éliminent la double imposition, avec des règles de départage de la résidence en cas de conflit.
        </p>
        <p>
          Côté pratique, l'identifiant fiscal d'un particulier sans activité est le PESEL. La déclaration annuelle passe par le
          formulaire adapté, par exemple le PIT-37 pour les revenus d'emploi.
        </p>
      </Section>

      {/* Récapitulatif par provenance */}
      <section id="recap" className="scroll-mt-24 mb-14" data-testid="demarches-recap">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mb-5">Récapitulatif par provenance</h2>
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm" data-testid="demarches-recap-table">
            <thead className="bg-slate-800 text-white">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Démarche</th>
                <th className="px-4 py-3 text-left font-semibold">UE et EEE</th>
                <th className="px-4 py-3 text-left font-semibold">Suisse</th>
                <th className="px-4 py-3 text-left font-semibold">Royaume-Uni (avant 2021)</th>
                <th className="px-4 py-3 text-left font-semibold">Royaume-Uni (après 2021)</th>
              </tr>
            </thead>
            <tbody>
              {RECAP_ROWS.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`recap-row-${i}`}>
                  <td className="px-4 py-3 font-medium text-slate-900 border-t border-slate-200">{row[0]}</td>
                  <td className="px-4 py-3 text-slate-700 border-t border-slate-200">{row[1]}</td>
                  <td className="px-4 py-3 text-slate-700 border-t border-slate-200">{row[2]}</td>
                  <td className="px-4 py-3 text-slate-700 border-t border-slate-200">{row[3]}</td>
                  <td className="px-4 py-3 text-slate-700 border-t border-slate-200">{row[4]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Sources */}
      <section id="sources" className="scroll-mt-24 mb-12" data-testid="demarches-sources">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mb-5">Sources</h2>
        <ol className="space-y-5 list-none">
          {SOURCES.map((s, i) => (
            <li key={s.url} className="flex items-baseline gap-3" data-testid={`source-${i + 1}`}>
              <span className="font-display text-sm font-semibold text-slate-400 tabular-nums w-6 flex-shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-start gap-1.5 text-[#0056D2] hover:underline break-words"
                  data-testid={`source-link-${i + 1}`}
                >
                  <span>{s.label}</span>
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 mt-1" />
                </a>
                <p className="text-sm text-slate-500 mt-1 leading-relaxed">{s.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Final disclaimer */}
      <Warn testid="warn-final">
        Cette page a une vocation d'information générale et ne remplace pas un conseil juridique ou fiscal individualisé. Les démarches
        et les seuils varient selon les offices et évoluent dans le temps. Reportez-vous aux sources officielles ci-dessus à la date de
        votre projet.
      </Warn>
    </div>
  );
}
